import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Paragraph, Skeleton, Tag } from '@digdir/designsystemet-react';
import { Link } from 'react-router-dom';
import { CreateCustomSurveyTemplateRequest, CustomSurveyTemplate } from '../../api/customSurveyTemplates';
import { Routes } from '../../enums/Routes';
import { buildRoute } from '../../utils/routeBuilder';
import { useGetCustomSurveyTemplateByIdQuery } from '../../hooks/queries/useGetCustomSurveyTemplateByIdQuery';
import { useCheckCustomSurveyTemplateUsageQuery } from '../../hooks/queries/useCheckCustomSurveyTemplateUsageQuery';
import { useUpdateCustomSurveyTemplateMutation } from '../../hooks/mutations/useUpdateCustomSurveyTemplateMutation';
import { useCreateCustomSurveyTemplateVersionMutation } from '../../hooks/mutations/useCreateCustomSurveyTemplateVersionMutation';
import { TemplateForm } from '../CreateCustomSurveyTemplatePage/components/TemplateForm';
import { fromTemplate } from '../CreateCustomSurveyTemplatePage/templateDraft';
import classes from '../CreateCustomSurveyTemplatePage/CreateCustomSurveyTemplatePage.module.css';

export const CustomSurveyTemplateEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const id = templateId ? Number(templateId) : null;

  const { data: template, isLoading, error } = useGetCustomSurveyTemplateByIdQuery(id);
  const usage = useCheckCustomSurveyTemplateUsageQuery(id);
  const updateMutation = useUpdateCustomSurveyTemplateMutation();
  const versionMutation = useCreateCustomSurveyTemplateVersionMutation();

  if (isLoading || usage.isLoading) {
    return (
      <div className={classes.page}>
        <span className="ds-sr-only">{t('customSurveys.templateEdit.loading')}</span>
        <Skeleton variant="rectangle" height="120px" />
        <Skeleton variant="rectangle" height="360px" />
      </div>
    );
  }

  if (error || !template || id === null) {
    return (
      <div className={classes.page}>
        <nav aria-label={t('breadcrumbs.ariaLabel')} className="ds-breadcrumbs">
          <ol>
            <li>
              <Link to={Routes.CustomSurveyTemplates} className="ds-link">
                {t('customSurveys.templateDetail.breadcrumb')}
              </Link>
            </li>
          </ol>
        </nav>
        <Alert data-color={error ? 'danger' : 'warning'}>
          <Heading level={2} data-size="xs">
            {t(error ? 'customSurveys.templateDetail.error.title' : 'customSurveys.templateDetail.notFound.title')}
          </Heading>
          <Paragraph>
            {t(error ? 'customSurveys.templateDetail.error.message' : 'customSurveys.templateDetail.notFound.message')}
          </Paragraph>
        </Alert>
      </div>
    );
  }

  const inUse = usage.data?.isInUse === true;
  const nextVersion = template.version + 1;
  const detailPath = (saved: CustomSurveyTemplate) => buildRoute(Routes.CustomSurveyTemplateDetail, { templateId: saved.id });

  const save = (request: CreateCustomSurveyTemplateRequest) => {
    if (inUse) {
      versionMutation.mutate(
        { id, data: request },
        { onSuccess: (saved) => navigate(detailPath(saved), { state: { savedVersion: saved.version } }) },
      );
    } else {
      updateMutation.mutate(
        { id, data: request },
        { onSuccess: (saved) => navigate(detailPath(saved), { state: { saved: true } }) },
      );
    }
  };

  return (
    <TemplateForm
      key={template.id}
      mode="edit"
      initialDraft={fromTemplate(template)}
      crumbs={[
        { label: t('customSurveys.templateDetail.breadcrumb'), to: Routes.CustomSurveyTemplates },
        { label: template.name, to: buildRoute(Routes.EditCustomSurveyTemplate, { templateId: id }) },
      ]}
      eyebrow={t('customSurveys.templateEdit.eyebrow')}
      fallbackTitle={template.name}
      titleExtra={
        <Tag data-size="sm" data-color="neutral" className={classes.versionTag}>
          {t('customSurveys.templateDetail.version', { version: template.version })}
        </Tag>
      }
      intro={
        inUse &&
        usage.data && (
          <Alert data-color="info">
            <Heading level={2} data-size="2xs">
              {t('customSurveys.templateEdit.newVersion.title', { version: nextVersion })}
            </Heading>
            <Paragraph>
              {t('customSurveys.templateEdit.newVersion.message', {
                surveys: t('customSurveys.templateEdit.newVersion.surveys', { count: usage.data.surveysCount }),
                scheduled: t('customSurveys.templateEdit.newVersion.scheduled', { count: usage.data.scheduledSurveysCount }),
                version: template.version,
              })}
            </Paragraph>
          </Alert>
        )
      }
      submitLabel={
        inUse ? t('customSurveys.templateEdit.saveAsVersion', { version: nextVersion }) : t('customSurveys.templateEdit.save')
      }
      isPending={updateMutation.isPending || versionMutation.isPending}
      saveFailed={updateMutation.isError || versionMutation.isError}
      cancelTo={buildRoute(Routes.CustomSurveyTemplateDetail, { templateId: id })}
      onSubmit={save}
    />
  );
};
